import React, { useState, useMemo } from 'react';
import { db } from '../../firebase';
import {
  doc, updateDoc, serverTimestamp, arrayUnion,
} from 'firebase/firestore';
import { useAuthorDashboard } from '../AuthorDashboardContext';
import { updateArcStatus } from '../../utils/updateArcStatus';
import {
  getArcStatusBadgeClass, getArcStatusBgClass, getArcStatusLabel,
} from '../../constants/arcStatus';
import KindleMissingDialog from '../../components/KindleMissingDialog';

/**
 * ARC Requests tab — tenant-scoped equivalent of the superadmin CRM
 * "ARC Requests" tab (App.js arc-requests). Reads pending applications
 * from authors/{authorId}/campaigns/{id}/applications via the dashboard
 * context (no top-level arcRequests collection access). Approve / Deny /
 * Approve All mirror App.js handlers but route via updateArcStatus, which
 * already handles tenant-scoped writes.
 */
export default function AuthorArcRequestsTab() {
  const { authorId, books, readers, bookApplications } = useAuthorDashboard();

  // Flatten bookApplications into a single list with campaign + book context
  const allApplications = useMemo(() => {
    const list = [];
    Object.entries(bookApplications || {}).forEach(([bookId, apps]) => {
      (apps || []).forEach(app => {
        list.push({ ...app, bookId: app.bookId || bookId });
      });
    });
    return list;
  }, [bookApplications]);

  const pending = allApplications.filter(a => (a.status || 'pending') === 'pending');
  const approved = allApplications.filter(a => a.status === 'approved');

  const [expandedMessages, setExpandedMessages] = useState(new Set());
  const [busyId, setBusyId] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [kindlePromptApp, setKindlePromptApp] = useState(null); // application doc when prompting

  function toggleMessageExpand(id) {
    setExpandedMessages(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  // Approve a single application — mirrors App.js:1753 handleApproveArcRequest
  async function approveApplication(app) {
    if (!app || busyId === app.id) return;
    setBusyId(app.id);
    try {
      // Update the reader's global doc — add bookId to booksReceived,
      // mark hasApprovedArcRequest. Status field is NOT changed.
      const reader = readers.find(r => r.id === app.readerId)
        || readers.find(r => (r.emailCorrespondence || '').toLowerCase() === (app.readerEmail || '').toLowerCase());
      if (reader) {
        const currentBooks = reader.booksReceived || [];
        const updates = {
          hasApprovedArcRequest: true,
          lastModified: serverTimestamp(),
        };
        if (!currentBooks.includes(app.bookId)) {
          updates.booksReceived = arrayUnion(app.bookId);
        }
        await updateDoc(doc(db, 'readers', reader.id), updates);
      }

      await updateArcStatus({
        authorId,
        campaignId: app.campaignId,
        applicationId: app.id,
        readerId: app.readerId,
        bookId: app.bookId,
        newStatus: 'approved',
      });
    } catch (err) {
      alert('Error approving request: ' + err.message);
    } finally {
      setBusyId(null);
    }
  }

  // Click handler: if the reader has no Kindle email on profile, surface
  // the prompt before approving. The prompt allows proceeding.
  function handleApproveClick(app) {
    const reader = readers.find(r => r.id === app.readerId)
      || readers.find(r => (r.emailCorrespondence || '').toLowerCase() === (app.readerEmail || '').toLowerCase());
    const kindle = (reader?.eReaderEmail || reader?.kindleEmail || '').trim();
    if (!kindle) {
      setKindlePromptApp(app);
      return;
    }
    approveApplication(app);
  }

  async function handleDenyClick(app) {
    if (!app || busyId === app.id) return;
    if (!window.confirm('Decline this ARC request?')) return;
    setBusyId(app.id);
    try {
      await updateArcStatus({
        authorId,
        campaignId: app.campaignId,
        applicationId: app.id,
        readerId: app.readerId,
        bookId: app.bookId,
        newStatus: 'declined',
      });
    } catch (err) {
      alert('Error declining request: ' + err.message);
    } finally {
      setBusyId(null);
    }
  }

  async function handleApproveAll() {
    if (pending.length === 0) { alert('No pending ARC requests to approve.'); return; }
    if (!window.confirm(`Approve all ${pending.length} pending ARC request${pending.length > 1 ? 's' : ''}? Reader statuses will not be changed.`)) return;
    setBulkBusy(true);
    let approvedCount = 0, errors = 0;
    for (const app of pending) {
      try {
        // Skip the kindle-missing prompt during bulk approve — proceed all.
        await approveApplication(app);
        approvedCount++;
      } catch {
        errors++;
      }
    }
    setBulkBusy(false);
    alert(`✅ Done! ${approvedCount} request${approvedCount === 1 ? '' : 's'} approved${errors > 0 ? `, ${errors} failed` : ''}.`);
  }

  // Sort: pending first, then by appliedAt descending
  const sorted = [...allApplications].sort((a, b) => {
    const aPending = (a.status || 'pending') === 'pending';
    const bPending = (b.status || 'pending') === 'pending';
    if (aPending && !bPending) return -1;
    if (!aPending && bPending) return 1;
    const aMs = a.appliedAt?.seconds || 0;
    const bMs = b.appliedAt?.seconds || 0;
    return bMs - aMs;
  });

  return (
    <>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-xl font-bold text-slate-900">ARC Requests</h2>
        <div className="flex items-center gap-2 text-sm">
          <span className="rounded-full bg-slate-100 px-3 py-1 font-medium text-teal-700">
            Pending: {pending.length}
          </span>
          <span className="rounded-full bg-green-100 px-3 py-1 font-medium text-green-700">
            Approved: {approved.length}
          </span>
          {pending.length > 0 && (
            <button
              onClick={handleApproveAll}
              disabled={bulkBusy}
              className="rounded-full bg-gradient-to-r from-slate-500 to-teal-600 px-4 py-1 font-semibold text-white hover:from-teal-600 hover:to-teal-700 transition-all shadow-sm disabled:opacity-60"
            >
              {bulkBusy ? 'Approving…' : `✅ Approve All (${pending.length})`}
            </button>
          )}
        </div>
      </div>

      {sorted.length === 0 ? (
        <div className="rounded-2xl border border-slate-100 bg-white p-12 text-center text-sm text-slate-500">
          No ARC requests yet.
        </div>
      ) : (
        <div className="space-y-3">
          {sorted.map((app) => {
            const book = books.find(b => b.id === app.bookId);
            const reader = readers.find(r => r.id === app.readerId)
              || readers.find(r => (r.emailCorrespondence || '').toLowerCase() === (app.readerEmail || '').toLowerCase());
            const kindleEmail = (reader?.eReaderEmail || reader?.kindleEmail || '').trim();
            const social = reader?.socialMedia || {};
            const populatedSocials = Object.entries(social).filter(([, v]) => v && String(v).trim());

            const isExpanded = expandedMessages.has(app.id);
            const isLong = (app.message || '').length > 150;
            const messageDisplay = isLong && !isExpanded
              ? `${app.message.slice(0, 150).trimEnd()}…`
              : (app.message || '');

            return (
              <div
                key={app.id}
                className={`rounded-2xl border p-4 shadow-sm ${getArcStatusBgClass(app.status)}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="mb-2 flex items-center gap-3 flex-wrap">
                      <h3 className="text-lg font-bold text-slate-900">
                        {app.readerName || reader?.name || 'Unknown reader'}
                      </h3>
                      <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold border ${getArcStatusBadgeClass(app.status)}`}>
                        {getArcStatusLabel(app.status)}
                      </span>
                      {!kindleEmail && (
                        <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-800 border border-amber-200">
                          ⚠ No Kindle email
                        </span>
                      )}
                    </div>

                    <p className="mb-1 text-sm text-slate-600">
                      <strong>Book:</strong> {book?.title || app.bookTitle || app.bookId}
                    </p>

                    <p className="mb-1 text-sm text-slate-600">
                      <strong>Email:</strong> {app.readerEmail || reader?.emailCorrespondence || '—'}
                    </p>

                    <p className="mb-2 text-sm text-slate-600">
                      <strong>Kindle:</strong>{' '}
                      {kindleEmail ? (
                        <span className="text-slate-700">{kindleEmail}</span>
                      ) : (
                        <span className="italic text-amber-700">Not provided on profile</span>
                      )}
                    </p>

                    {app.message ? (
                      <div className="mb-3 rounded-lg bg-white p-3">
                        <p className="mb-1 text-xs font-semibold text-slate-500">Why they want it:</p>
                        <p className="text-sm text-slate-700 whitespace-pre-wrap">
                          {messageDisplay}
                          {isLong && (
                            <>
                              {' '}
                              <button
                                type="button"
                                aria-expanded={isExpanded}
                                onClick={() => toggleMessageExpand(app.id)}
                                className="font-medium text-teal-700 hover:text-slate-900"
                              >
                                {isExpanded ? 'Show less' : 'Show more'}
                              </button>
                            </>
                          )}
                        </p>
                      </div>
                    ) : (
                      <p className="mb-3 text-sm italic text-slate-400">No message provided.</p>
                    )}

                    <div className="flex flex-wrap gap-3 text-xs text-slate-500">
                      <span>📊 {(app.totalReviews ?? reader?.totalReviews ?? 0)} reviews submitted</span>
                      <span>⭐ {(app.totalPoints ?? reader?.totalPoints ?? 0)} points earned</span>
                      <span>📅 Applied {app.appliedAt?.toDate ? app.appliedAt.toDate().toLocaleDateString('en-GB') : '—'}</span>
                    </div>

                    {populatedSocials.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2 text-xs text-slate-500">
                        {populatedSocials.map(([key, value]) => (
                          <span key={key} className="rounded-full bg-white border border-slate-200 px-2 py-0.5">
                            <strong className="capitalize">{key}:</strong> {String(value)}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {(app.status || 'pending') === 'pending' && (
                    <div className="flex gap-2 shrink-0">
                      <button
                        onClick={() => handleApproveClick(app)}
                        disabled={busyId === app.id || bulkBusy}
                        className="rounded-lg bg-green-600 px-4 py-2 text-xs font-semibold text-white hover:bg-green-700 disabled:opacity-60"
                      >
                        {busyId === app.id ? 'Saving…' : 'Approve'}
                      </button>
                      <button
                        onClick={() => handleDenyClick(app)}
                        disabled={busyId === app.id || bulkBusy}
                        className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                      >
                        Deny
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <KindleMissingDialog
        open={!!kindlePromptApp}
        readerName={kindlePromptApp?.readerName || readers.find(r => r.id === kindlePromptApp?.readerId)?.name}
        onCancel={() => setKindlePromptApp(null)}
        onProceed={() => {
          const app = kindlePromptApp;
          setKindlePromptApp(null);
          if (app) approveApplication(app);
        }}
        proceedLabel="Approve anyway"
      />
    </>
  );
}
